import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Dialog,
  IconButton,
  Typography,
  makeStyles,
  withStyles,
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";

import { addEntityLink, updateEntityLink } from "../../../../API/Entity";
import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import TextField from "../../../../Component/Forms/TextField";
import TextArea from "../../../../Component/Forms/TextArea";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../Component/CustomButton";

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const CreateLink = (props) => {
  const { open, onclose, entity, type, link = null } = props;
  const classes = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(open);
  const [linkId, setLinkId] = useState();
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState();
  const [urlErrorMessage, setUrlErrorMessage] = useState();
  const [isNewLink, setNewLink] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { showErrorMessage, showMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();
  const { id: entityId } = entity || {};

  useEffect(() => {
    if (open) {
      setIsDialogOpen(true);
      setNewLink(link === null);
      if (link) {
        const { id, url = null, description = null } = link;
        setLinkId(id);
        setUrl(url);
        setDescription(description);
      }
    }
  }, [open, link]);

  const handleClose = (status) => {
    setIsDialogOpen(false);
    setLinkId();
    setUrl();
    setDescription();
    if (onclose) {
      onclose(status);
    }
  };

  const cancel = () => {
    handleClose("cancel");
  }

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const validateLink = () => {
    if (!url || url === "") {
      setUrlErrorMessage(t("messages.record.links.urlError"));
      return false;
    }
    return true;
  };

  const updateExistingLink = async () => {
    if (!validateLink()) {
      return;
    }
    setIsLoading(true);
    try {
      const results = await updateEntityLink({
        id: linkId,
        url,
        description,
        entityId,
        type,
      });
      const { message } = results;
      showMessage({ message });
      handleClose();
    } catch (error) {
      showErrorMessage({ error });
    }
    handleClose();
  };

  const submitLink = async () => {
    if (!validateLink()) {
      return;
    }
    setIsLoading(true);

    try {
      const results = await addEntityLink({
        entityId,
        url,
        description,
        type,
      });
      const { message } = results;
      showMessage({ message });
      handleClose();
    } catch (error) {
      showErrorMessage({ error });
    }
    handleClose();
  };

  const renderCreateForm = () => {
    return (
      <Grid container direction="row" spacing={3}>
        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.links.form.url")}
            initialValue={url === null || url === undefined ? "" : url}
            value={url}
            type={"text"}
            onChange={setUrl}
            required={true}
          />
          <div className={classes.errorText}>{urlErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={12} xs={12}>
          <TextArea
            label={t("labels.record.links.form.description")}
            rowsMax={3}
            placeholder={t("labels.record.links.form.describeYourLink")}
            initialValue={description}
            onChange={setDescription}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Dialog
        disableBackdropClick
        onClose={cancel}
        aria-labelledby="add-document-modal"
        open={isDialogOpen}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle id="add-document-modal" onClose={cancel}>
          <Typography className={classes.panelTitle}>{t("labels.record.links.form.header")}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? renderLoadingIndicator() : renderCreateForm()}
        </DialogContent>

        <DialogActions>
          <CustomButton 
            variant="secondary" 
            onClick={cancel}
          >
            {t("labels.common.close")}
          </CustomButton>
          {isNewLink && (
            <CustomButton 
              variant="primary" 
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={submitLink}
            >
              {t("labels.common.submit")}
            </CustomButton>
            
          )}
          {!isNewLink && (
            <CustomButton 
              variant="primary" 
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={updateExistingLink}
            >
              {t("labels.common.update")}
            </CustomButton>
            
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateLink;
